import Vue from 'vue'
import { Module, MutationTree, ActionTree, GetterTree } from 'vuex'

import DictionaryMapper from '../mapping/dictionary-mapper'

import { IDictionary } from '@/shared/models/interface/dictionary-model'
import { IImpersonationConfig } from '../models/config-interface'
import { FilterGroup, FilterItem } from '../models/filter-viewmodel'
import { IWidgetState } from '.'

import SearchService from '../services/search-service'

interface IImpersonationState {
    config: IImpersonationConfig
    error: boolean
    loading: boolean
    searchService: SearchService
    selectedOptions: IDictionary<string>
    studies: IDictionary<string>
}

class ImpersonationState implements IImpersonationState {
    config: IImpersonationConfig = {
        filters: {},
        labels: {},
        language: '',
        searchIndex: ''
    }
    error: boolean = false
    loading: boolean = false
    searchService!: SearchService
    selectedOptions: IDictionary<string> = {}
    studies: IDictionary<string> = {}
}

const mutations: MutationTree<IImpersonationState> = {
    setConfig(state, config: IImpersonationConfig) {
        state.config = config
    },

    setError(state, throwError: boolean) {
        state.error = throwError
    },

    setLoading(state, isLoading: boolean) {
        state.loading = isLoading
    },

    setSearchService(state) {
        state.searchService = process.env.NODE_ENV === 'development'
            ? new SearchService(state.config.searchIndex, 'http://reverseproxy:5013')
            : new SearchService(state.config.searchIndex)
    },

    setSelectedOptions(state, options: IDictionary<string>) {
        state.selectedOptions = { ...options }
    },

    setSelectedOption(state, [key, value]) {
        Vue.set(state.selectedOptions, key, value)
    },

    resetSelectedOption(state, key: string) {
        Vue.delete(state.selectedOptions, key)
    },

    setStudies(state, studies: IDictionary<string>) {
        Vue.set(state, 'studies', studies)
    },

    resetStudies(state) {
        Vue.delete(state, 'studies')
    }
}

const actions: ActionTree<IImpersonationState, IWidgetState> = {
    init(context, payload) {
        context.commit('setConfig', payload)
        context.commit('setSearchService')
        context.commit('setSelectedOptions', context.getters.filteredSettings)
    },

    changeSelection(context, payload) {
        context.commit('setSelectedOption', payload)

        if (context.getters.showStudySelector) {
            context.dispatch('loadStudies')
        }
    },

    save(context) {
        Object.entries(context.state.selectedOptions).forEach(([key, value]) => {
            context.commit('updateSetting', [key, value], { root: true })
        })
    },

    reset(context) {
        Object.keys(context.state.selectedOptions).forEach((key: string) => {
            context.commit('removeSetting', key, { root: true })
            context.commit('resetSelectedOption', key)
        })

        context.commit('resetStudies')
        context.commit('setError', false)
        context.commit('setLoading', false)
    },

    loadStudies(context) {
        context.commit('setLoading', true)
        context.commit('setError', false)

        context.state.searchService.setSearchQuery(context.state.config.language)
        context.state.searchService.loadData()
            .then((response: IDictionary<string>) => {
                context.commit('setStudies', response)
            }).catch((error: any) => {
                console.error(error)
                context.commit('setError', true)
            }).finally(() => {
                context.commit('setLoading', false)
            })
    }
}

const getters: GetterTree<IImpersonationState, IWidgetState> = {
    filters(state): IDictionary<FilterGroup> {
        return state.config.filters
    },

    filteredSettings(_, getters, rootState): IDictionary<string> {
        let keys: string[] = Object.keys(getters.filters)
        let settings: IDictionary<string>[] = Object.keys(rootState.settings)
            .filter((x: string) => keys.includes(x))
            .map((x: string) => ({ [x]: rootState.settings[x] } as IDictionary<string>))

        return Object.assign(
            {},
            ...settings
        )
    },

    labels(state): IDictionary<string> {
        return state.config.labels
    },

    formIsValid(state): boolean {
        return Object.keys(state.selectedOptions).includes('audience')
    },

    canReset(state): boolean {
        return Object.keys(state.selectedOptions).some((key: string) =>
            state.selectedOptions[key] != null
        )
    },

    showStudySelector(state): boolean {
        return state.selectedOptions['studyType'] ? !!state.selectedOptions['studyType'].match(/^(bachelor|master|premaster)$/) : false
    },

    hasStudies(state): boolean {
        return Object.keys(state.studies)?.length > 0
    },

    dataIsModified(state, _, rootState): boolean {
        return Object.keys(state.selectedOptions).some((key: string) =>
            rootState.settings[key] != state.selectedOptions[key]
        )
    },

    selectedOption: (state) => (id: string) => {
        return state.selectedOptions[id]
    },

    options: (state, getters) => (id: string) => {
        if (id === 'study') {
            return state.studies
        }

        let filterItems: FilterItem[] = getters.filters[id]?.FilterItems
        return DictionaryMapper.mapFilterItems(filterItems)
    }
}

export const impersonation: Module<IImpersonationState, any> = {
    namespaced: true,
    state: new ImpersonationState,
    mutations,
    actions,
    getters,
}